// Adaptador: adminLive.ts → consume del motor contentMaster.ts
// Exporta las mismas interfaces y datos que esperan las páginas.

import {
  canonicalLives,
  canonicalRecordings,
  getPhotoUrl,
  type CanonicalLive,
  type CanonicalRecording,
} from './contentMaster';

// ─── Tipos Legacy ──────────────────────────────────────────

export type LiveStatus = 'draft' | 'scheduled' | 'en_vivo' | 'finalizado' | 'recording_available' | 'published' | 'archived';
export type LiveProvider = 'youtube' | 'facebook' | 'vps' | 'mux';

export interface AdminLive {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  host: string;
  imageUrl: string;
  provider: LiveProvider;
  providerUrl: string;
  status: LiveStatus;
  createdAt: string;
  // Post-live lineage
  generatedVideoId?: number;
  generatedPostId?: number;
  generatedRecordingId?: number;
}

export interface AdminLiveRecording {
  id: number;
  liveId: number;
  title: string;
  description: string;
  hostDisplayName: string;
  programName?: string;
  date: string;
  duration: string;
  thumbnailUrl: string;
  platform: string;
  audiencePeak: number;
  audienceAvg: number;
  totalViews: number;
  status: 'processing' | 'available';
  createdAt: string;
}

export interface AdminLiveData {
  lives: AdminLive[];
}

// ─── Labels ────────────────────────────────────────────────

export const statusLabels: Record<LiveStatus, string> = {
  draft: 'Borrador',
  scheduled: 'Programado',
  en_vivo: 'En vivo',
  finalizado: 'Finalizado',
  recording_available: 'Grabación disponible',
  published: 'Publicado',
  archived: 'Archivado',
};

export const statusColors: Record<LiveStatus, { bg: string; text: string; dot: string }> = {
  draft: { bg: 'bg-foreground-100', text: 'text-foreground-500', dot: 'bg-foreground-400' },
  scheduled: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-400' },
  en_vivo: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  finalizado: { bg: 'bg-foreground-200', text: 'text-foreground-600', dot: 'bg-foreground-400' },
  recording_available: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  published: { bg: 'bg-accent-100', text: 'text-accent-700', dot: 'bg-accent-500' },
  archived: { bg: 'bg-foreground-200', text: 'text-foreground-500', dot: 'bg-foreground-500' },
};

export const providerLabels: Record<LiveProvider, string> = {
  youtube: 'YouTube Live',
  facebook: 'Facebook Live',
  vps: 'VPS Propio',
  mux: 'Mux',
};

export const providerIcons: Record<LiveProvider, string> = {
  youtube: 'ri-youtube-fill',
  facebook: 'ri-facebook-circle-fill',
  vps: 'ri-server-line',
  mux: 'ri-live-line',
};

export const providerColors: Record<LiveProvider, { bg: string; text: string }> = {
  youtube: { bg: 'bg-red-100', text: 'text-red-600' },
  facebook: { bg: 'bg-blue-100', text: 'text-blue-600' },
  vps: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  mux: { bg: 'bg-violet-100', text: 'text-violet-600' },
};

// ─── Mapeo ────────────────────────────────────────────────

function toLegacyLiveId(canonicalId: string): number {
  const match = canonicalId.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

function mapLiveStatus(s: CanonicalLive['status']): LiveStatus {
  switch (s) {
    case 'draft': return 'draft';
    case 'scheduled': return 'scheduled';
    case 'live': return 'en_vivo';
    case 'ended': return 'finalizado';
    case 'recording_available': return 'recording_available';
    case 'published': return 'published';
    case 'archived': return 'archived';
    default: return 'finalizado';
  }
}

function mapLive(l: CanonicalLive): AdminLive {
  return {
    id: toLegacyLiveId(l.id),
    title: l.title,
    description: l.description,
    date: l.date,
    time: l.time,
    host: l.hostDisplayName,
    imageUrl: getPhotoUrl(l.thumbnailPhotoId),
    provider: l.provider as LiveProvider,
    providerUrl: l.providerUrl,
    status: mapLiveStatus(l.status),
    createdAt: l.createdAt,
    generatedVideoId: l.generatedVideoId ? toLegacyLiveId(l.generatedVideoId) : undefined,
    generatedPostId: l.generatedPostId ? toLegacyLiveId(l.generatedPostId) : undefined,
    generatedRecordingId: l.generatedRecordingId ? toLegacyLiveId(l.generatedRecordingId) : undefined,
  };
}

const adaptedLives: AdminLive[] = canonicalLives.map(mapLive);

export const adminLiveData: AdminLiveData = {
  lives: adaptedLives,
};

// ─── Recordings adapter ────────────────────────────────────

function mapRecording(r: CanonicalRecording): AdminLiveRecording {
  return {
    id: toLegacyLiveId(r.id),
    liveId: toLegacyLiveId(r.liveId),
    title: r.title,
    description: r.description,
    hostDisplayName: r.hostDisplayName,
    programName: r.programName,
    date: r.date,
    duration: r.duration,
    thumbnailUrl: getPhotoUrl(r.thumbnailPhotoId),
    platform: r.platform,
    audiencePeak: r.audiencePeak,
    audienceAvg: r.audienceAvg,
    totalViews: r.totalViews,
    status: r.status,
    createdAt: r.createdAt,
  };
}

export const adminRecordings: AdminLiveRecording[] = canonicalRecordings.map(mapRecording);

export function getRecordingByLiveId(liveId: number): AdminLiveRecording | undefined {
  return adminRecordings.find((r) => r.liveId === liveId);
}